import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/auth';
import { KhiimoriLogo } from '@/components/brand/KhiimoriLogo';

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const completeOAuthSession = useAuthStore((s) => s.completeOAuthSession);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const err = searchParams.get('error');
    if (err) {
      setError(err);
      toast.error(err);
      return;
    }

    const token = searchParams.get('token');
    const refreshToken = searchParams.get('refresh_token');
    const expiresAt = searchParams.get('expires_at');

    if (!token || !refreshToken || !expiresAt) {
      setError('Sign-in response was incomplete');
      return;
    }

    (async () => {
      try {
        completeOAuthSession({ token, refresh_token: refreshToken, expires_at: expiresAt });
        const user = await authService.getUser();
        useAuthStore.getState().setUser(user);
        toast.success('Signed in with Google');
        navigate('/home', { replace: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to complete sign-in');
        toast.error('Failed to complete Google sign-in');
      }
    })();
  }, [searchParams, completeOAuthSession, navigate]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-khiimori-bg-primary px-4">
      <div className="text-center max-w-sm">
        <div className="flex justify-center mb-4">
          <KhiimoriLogo size={56} className="rounded-xl" />
        </div>
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-khiimori-text-primary mb-2">Sign-in failed</h1>
            <p className="text-sm text-khiimori-text-muted mb-6">{error}</p>
            <Link to="/login" className="text-primary-400 hover:text-primary-300 text-sm font-medium">
              Back to sign in
            </Link>
          </>
        ) : (
          <>
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
            <p className="text-sm text-khiimori-text-muted">Completing sign-in…</p>
          </>
        )}
      </div>
    </main>
  );
}
